import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Avatar,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  LinearProgress,
} from "@mui/material";
import {
  Lock as LockIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { ResetPasswordResponse } from "../../services/usersService";
import { NotificationManager } from "react-notifications";
import { useNavigate } from "react-router-dom";

const TakeResetPassword = ({ tempToken }) => {
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null); // 'success', 'error', or null
  const [message, setMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Password strength validation
  const checkPasswordStrength = (password) => {
    let strength = 0;
    const checks = [
      password.length >= 8, // At least 8 characters
      /[a-z]/.test(password), // Lowercase letter
      /[A-Z]/.test(password), // Uppercase letter
      /\d/.test(password), // Number
      /[!@#$%^&*(),.?":{}|<>]/.test(password), // Special character
    ];
    
    strength = checks.filter(Boolean).length;
    return strength;
  };

  const getPasswordStrengthLabel = (strength) => {
    switch (strength) {
      case 0:
      case 1:
        return "Very Weak";
      case 2:
        return "Weak";
      case 3:
        return "Fair";
      case 4:
        return "Good";
      case 5:
        return "Strong";
      default:
        return "";
    }
  };

  const getPasswordStrengthColor = (strength) => {
    switch (strength) {
      case 0:
      case 1:
        return "error";
      case 2:
        return "warning";
      case 3:
        return "info";
      case 4:
      case 5:
        return "success";
      default:
        return "primary";
    }
  };

  const validatePassword = (password) => {
    if (!password) {
      return "Password is required";
    }
    if (password.length < 8) {
      return "Password must be at least 8 characters long";
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return "Password must contain at least one lowercase letter";
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return "Password must contain at least one uppercase letter";
    }
    if (!/(?=.*\d)/.test(password)) {
      return "Password must contain at least one number";
    }
    if (!/(?=.*[!@#$%^&*(),.?":{}|<>])/.test(password)) {
      return "Password must contain at least one special character";
    }
    return "";
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);

    // Clear previous errors
    setPasswordError("");
    setStatus(null);
    setMessage("");

    // Check password strength
    const strength = checkPasswordStrength(value);
    setPasswordStrength(strength);

    // Validate password
    if (value) {
      const error = validatePassword(value);
      setPasswordError(error);
    }

    // Revalidate confirm password if it exists
    if (confirmPassword && value !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
    } else if (confirmPassword && value === confirmPassword) {
      setConfirmPasswordError("");
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);

    // Clear previous errors
    setConfirmPasswordError("");
    setStatus(null);
    setMessage("");

    // Validate confirm password
    if (value && value !== password) {
      setConfirmPasswordError("Passwords do not match");
    }
  };

  const handleResetPassword = async () => {
    // Validate all fields before submission
    if (!password) {
      setPasswordError("Password is required");
      return;
    }

    if (!confirmPassword) {
      setConfirmPasswordError("Please confirm your password");
      return;
    }

    const passwordValidationError = validatePassword(password);
    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      return;
    }

    if (passwordStrength < 4) {
      setPasswordError("Please choose a stronger password");
      return;
    }

    if (!tempToken) {
      setStatus("error");
      setMessage("Invalid or expired reset token. Please request a new password reset link.");
      return;
    }

    setIsLoading(true);
    setStatus(null);
    setMessage("");

    try {
      // Send the reset password request with token and new password
      const resetData = {
        token: tempToken,
        password: password
      };

      let response = await ResetPasswordResponse(resetData);

      NotificationManager.success("Password changed successfully.");

      setStatus("success");
      setMessage("Your password has been successfully reset. You can now log in with your new password.");
      
      // Clear form on success
      setPassword("");
      setConfirmPassword("");
      setPasswordStrength(0);
      
    } catch (error) {
      console.log("Error: ", error);

  const errorMessage =
    error.response?.data ||
    "Error occurred while resetting password.";

  NotificationManager.error("Error", errorMessage);

  setStatus("error");
  setMessage(
    errorMessage ||
    "Failed to reset password. The link may be expired or invalid. Please request a new password reset link."
  );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate("/");
     window.location.reload();
  };

  const resetForm = () => {
    setPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setConfirmPasswordError("");
    setPasswordStrength(0);
    setStatus(null);
    setMessage("");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #e3f2fd 0%, #ffffff 50%, #f3e5f5 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        {/* Header Section */}
        <Box textAlign="center" mb={4}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: "primary.main",
              mb: 2,
              mx: "auto",
              background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
            }}
          >
            <LockIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography
            variant="h4"
            component="h1"
            fontWeight="bold"
            gutterBottom
          >
            Reset Password
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Enter your new password below.
          </Typography>
        </Box>

        {/* Main Card */}
        <Paper
          elevation={8}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {status === "success" ? (
              /* Success State */
              <Box textAlign="center">
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: "success.light",
                    mb: 3,
                    mx: "auto",
                  }}
                >
                  <CheckCircleIcon
                    sx={{ fontSize: 32, color: "success.main" }}
                  />
                </Avatar>
                <Typography variant="h6" fontWeight="semibold" gutterBottom>
                  Password Reset Successful
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3, lineHeight: 1.6 }}
                >
                  {message}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGoBack}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: "semibold",
                    boxShadow: 2,
                    "&:hover": {
                      boxShadow: 4,
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  Go to Login
                </Button>
              </Box>
            ) : (
              /* Form State */
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Error Alert */}
                {status === "error" && (
                  <Alert
                    severity="error"
                    icon={<ErrorOutlineIcon />}
                    sx={{
                      borderRadius: 2,
                      "& .MuiAlert-message": {
                        lineHeight: 1.6,
                      },
                    }}
                  >
                    {message}
                  </Alert>
                )}

                {/* New Password Input */}
                <Box>
                  <TextField
                    fullWidth
                    label="New Password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={handlePasswordChange}
                    error={!!passwordError}
                    helperText={passwordError}
                    disabled={isLoading}
                    placeholder="Enter your new password"
                    InputProps={{
                      startAdornment: (
                        <LockIcon
                          sx={{
                            color: passwordError ? "error.main" : "action.active",
                            mr: 1,
                          }}
                        />
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            disabled={isLoading}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        "&:hover": {
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderWidth: 2,
                          },
                        },
                        "&.Mui-focused": {
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderWidth: 2,
                          },
                        },
                      },
                    }}
                  />
                  
                  {/* Password Strength Indicator */}
                  {password && (
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Password Strength:
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color={`${getPasswordStrengthColor(passwordStrength)}.main`}
                          fontWeight="semibold"
                        >
                          {getPasswordStrengthLabel(passwordStrength)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(passwordStrength / 5) * 100}
                        color={getPasswordStrengthColor(passwordStrength)}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: "grey.200",
                        }}
                      />
                    </Box>
                  )}
                </Box>

                {/* Confirm Password Input */}
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  error={!!confirmPasswordError}
                  helperText={confirmPasswordError}
                  disabled={isLoading}
                  placeholder="Confirm your new password"
                  InputProps={{
                    startAdornment: (
                      <LockIcon
                        sx={{
                          color: confirmPasswordError ? "error.main" : "action.active",
                          mr: 1,
                        }}
                      />
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          disabled={isLoading}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderWidth: 2,
                        },
                      },
                      "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderWidth: 2,
                        },
                      },
                    },
                  }}
                />

                {/* Password Requirements */}
                <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
                  <Typography variant="caption" fontWeight="semibold" gutterBottom display="block">
                    Password Requirements:
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    • At least 8 characters long<br/>
                    • Contains uppercase and lowercase letters<br/>
                    • Contains at least one number<br/>
                    • Contains at least one special character (!@#$%^&*...)
                  </Typography>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleResetPassword}
                    disabled={
                      isLoading || 
                      !!passwordError || 
                      !!confirmPasswordError || 
                      !password || 
                      !confirmPassword ||
                      passwordStrength < 4
                    }
                    startIcon={
                      isLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : null
                    }
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: "semibold",
                      boxShadow: 2,
                      "&:hover": {
                        boxShadow: 4,
                        transform: "translateY(-1px)",
                      },
                      "&:disabled": {
                        transform: "none",
                      },
                    }}
                  >
                    {isLoading ? "Resetting Password..." : "Reset Password"}
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleGoBack}
                    disabled={isLoading}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: "semibold",
                    }}
                  >
                    Back to Login
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Paper>

        {/* Footer */}
        {status !== "success" && (
          <Box textAlign="center" mt={4}>
            <Typography variant="body2" color="text.secondary">
              Having trouble? Contact support for assistance.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default TakeResetPassword;